import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

interface SEOProps {
  title?: string;
  description?: string;
  canonical?: string;
  image?: string;
  type?: 'website' | 'article';
  robots?: string;
  themeColor?: string;
}

const SEO: React.FC<SEOProps> = ({ 
    title, 
    description, 
    canonical, 
    image = 'https://images.unsplash.com/photo-1556761175-5973dc0f32e7?auto=format&fit=crop&w=1200&h=630&q=80',
    type = 'website',
    robots = 'index, follow',
    themeColor = '#0A1128'
}) => {
  const location = useLocation();
  const url = `${window.location.origin}${location.pathname}`;

  useEffect(() => {
    // 1. Update Title
    if (title) {
      document.title = title;
      updateMeta('property', 'og:title', title);
      updateMeta('name', 'twitter:title', title);
    }

    // 2. Update Description
    if (description) {
      updateMeta('name', 'description', description);
      updateMeta('property', 'og:description', description);
      updateMeta('name', 'twitter:description', description);
    }

    // 3. Update Image
    if (image) {
      updateMeta('property', 'og:image', image);
      updateMeta('property', 'og:image:width', '1200');
      updateMeta('property', 'og:image:height', '630');
      updateMeta('name', 'twitter:image', image);
    }

    // 4. Update URL & Type
    updateMeta('property', 'og:url', url);
    updateMeta('property', 'og:type', type);
    updateMeta('property', 'og:site_name', 'KithHR');

    // 5. Canonical
    let linkCanonical = document.querySelector("link[rel='canonical']");
    if (!linkCanonical) {
        linkCanonical = document.createElement("link");
        linkCanonical.setAttribute("rel", "canonical");
        document.head.appendChild(linkCanonical);
    }
    linkCanonical.setAttribute("href", canonical || url);

    // 6. Robots
    updateMeta('name', 'robots', robots);

    // 7. Theme Color
    updateMeta('name', 'theme-color', themeColor);

    // 8. Dynamic BreadcrumbList JSON-LD
    let scriptBreadcrumb = document.querySelector("#dynamic-breadcrumb-jsonld");
    if (!scriptBreadcrumb) {
      scriptBreadcrumb = document.createElement("script");
      scriptBreadcrumb.setAttribute("id", "dynamic-breadcrumb-jsonld");
      scriptBreadcrumb.setAttribute("type", "application/ld+json");
      document.head.appendChild(scriptBreadcrumb);
    }

    const pathSegments = location.pathname.split('/').filter(Boolean);
    const breadcrumbItems = [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Home",
        "item": "https://kithhr.co.uk/"
      }
    ];

    if (pathSegments.length > 0) {
      let cumulativePath = '';
      pathSegments.forEach((segment, idx) => {
        cumulativePath += `/${segment}`;
        const segmentName = segment
          .split('-')
          .map(w => w.charAt(0).toUpperCase() + w.slice(1))
          .join(' ');
        
        breadcrumbItems.push({
          "@type": "ListItem",
          "position": idx + 2,
          "name": segmentName,
          "item": `https://kithhr.co.uk${cumulativePath}`
        });
      });
    }

    const breadcrumbSchema = {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": breadcrumbItems
    };

    scriptBreadcrumb.textContent = JSON.stringify(breadcrumbSchema);

  }, [title, description, image, url, type, canonical, robots, themeColor, location.pathname]);

  const updateMeta = (attr: 'name' | 'property', key: string, value: string) => {
      let element = document.querySelector(`meta[${attr}='${key}']`);
      if (!element) {
          element = document.createElement('meta');
          element.setAttribute(attr, key);
          document.head.appendChild(element);
      }
      element.setAttribute('content', value);
  };

  return null;
};

export default SEO;
